// Le panneau de détail (colonne droite) : en-tête promu (barre d'accent de la catégorie, grand
// titre, pastille d'état + chip, ligne créé/mis à jour), description, contenus liés, assignés
// éditables en place, fil de discussion groupé et composeur.
import React from "react";
import { Link } from "react-router-dom";
import { FiCheckCircle, FiExternalLink, FiLink, FiMessageSquare, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import { iconForContentType } from "../Publication/publicationUtils";
import { UserAvatar } from "../../Components/UserAvatar";
import GatedButton from "../../Components/GatedButton";
import { categoryTone } from "./ticketUi";
import { AuthorBadge, CategoryChip, RelativeTime, SectionLabel, StatusPill } from "./TicketBits";
import { AssigneeEditor } from "./AssigneeEditor";
import { TicketThread } from "./TicketThread";
import { TicketComposer } from "./TicketComposer";
import type { Ticket, TicketComment } from "./ticketsClient";

export const TicketDetail: React.FC<{
	detail: { ticket: Ticket; comments: TicketComment[] };
	categoryLabel: (key: string) => string;
	canWrite: boolean;
	currentUserEmail?: string | null;
	onToggleStatus: (ticket: Ticket, close: boolean) => void;
	onDelete: (ticket: Ticket) => void;
	/** Rechargement détail + liste après une édition d'assignés. */
	onAssigneesSaved: () => void;
	comment: string;
	onCommentChange: (next: string) => void;
	onSendComment: () => void;
	sendingComment: boolean;
}> = ({ detail, categoryLabel, canWrite, currentUserEmail, onToggleStatus, onDelete, onAssigneesSaved, comment, onCommentChange, onSendComment, sendingComment }) => {
	const { ticket, comments } = detail;
	const tone = categoryTone(ticket.category);
	const updatedAfterCreate = ticket.updatedAt && ticket.updatedAt !== ticket.createdAt;

	return (
		<section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:max-h-[calc(100vh-7.5rem)]">
			{/* Barre d'accent de la catégorie : le détail répond au rail des cartes de la liste. */}
			<span className={`block h-1 w-full ${tone.bar}`} aria-hidden />

			{/* En-tête du dossier */}
			<div className="border-b border-slate-100 bg-slate-50/70 px-5 py-4">
				<div className="flex items-start justify-between gap-3">
					<div className="flex min-w-0 items-start gap-3">
						<UserAvatar
							name={ticket.createdBy?.name ?? ticket.createdByEmail}
							id={ticket.createdBy?.id ?? undefined}
							avatarKey={ticket.createdBy?.avatarKey}
							pictureUrl={ticket.createdBy?.pictureUrl}
							sizeClass="h-11 w-11"
							textClass="text-sm"
							title={`Créé par ${ticket.createdBy?.name ?? ticket.createdByEmail}`}
						/>
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-1.5">
								<StatusPill status={ticket.status} />
								<CategoryChip categoryKey={ticket.category} label={categoryLabel(ticket.category)} />
							</div>
							<h2 className="mt-1.5 text-xl font-bold leading-snug text-slate-900">{ticket.title}</h2>
							<p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-400">
								Ouvert par <AuthorBadge author={ticket.createdBy} fallback={ticket.createdByEmail} /> ·{" "}
								<RelativeTime value={ticket.createdAt} />
								{updatedAfterCreate && (
									<>
										{" "}· Mis à jour <RelativeTime value={ticket.updatedAt} />
									</>
								)}
							</p>
							{ticket.status === "closed" && (ticket.closedBy || ticket.closedByEmail) && (
								<p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-slate-400">
									Fermé par <AuthorBadge author={ticket.closedBy} fallback={ticket.closedByEmail} /> ·{" "}
									<RelativeTime value={ticket.closedAt} />
								</p>
							)}
						</div>
					</div>
					{canWrite && (
						<div className="flex shrink-0 gap-1.5">
							{ticket.status === "open" ? (
								<button
									type="button"
									onClick={() => onToggleStatus(ticket, true)}
									className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
								>
									<FiCheckCircle className="h-3.5 w-3.5" /> Fermer
								</button>
							) : (
								<button
									type="button"
									onClick={() => onToggleStatus(ticket, false)}
									className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
								>
									<FiRotateCcw className="h-3.5 w-3.5" /> Rouvrir
								</button>
							)}
							{/* Suppression définitive : feature:tickets:purge : visible mais grisé + explication si la permission manque. */}
							<GatedButton
								token="feature:tickets:purge"
								onClick={() => onDelete(ticket)}
								title="Supprimer définitivement"
								className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 shadow-sm transition hover:border-rose-300 hover:bg-rose-50"
							>
								<FiTrash2 className="h-3.5 w-3.5" />
							</GatedButton>
						</div>
					)}
				</div>
			</div>

			{/* Corps du dossier : description, contenus liés, assignés, discussion */}
			<div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
				{ticket.description && (
					<p className="whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
						{ticket.description}
					</p>
				)}

				{/* Contenus liés : badges cliquables (icône + titre + type), posés à la création du ticket. */}
				<div>
					<SectionLabel icon={<FiLink className="h-3 w-3" />}>
						Contenus liés{(ticket.linkedContents?.length ?? 0) > 0 ? ` (${ticket.linkedContents!.length})` : ""}
					</SectionLabel>
					{(ticket.linkedContents?.length ?? 0) > 0 ? (
						<div className="grid gap-1.5 sm:grid-cols-2">
							{ticket.linkedContents!.map((c) => {
								const Icon = iconForContentType(c.title, c.contentType);
								return (
									<Link
										key={c.contentId}
										to={`/content/${encodeURIComponent(c.contentType ?? "")}/${encodeURIComponent(c.contentId)}`}
										className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 hover:shadow"
										title={c.title ?? c.contentId}
									>
										<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition group-hover:bg-sky-100 group-hover:text-sky-600">
											<Icon className="h-4 w-4" />
										</span>
										<span className="min-w-0 flex-1">
											<span className="block truncate text-sm font-semibold text-slate-800">{c.title || c.contentId}</span>
											{c.contentType && (
												<span className="block truncate text-[10px] uppercase tracking-wide text-slate-400">{c.contentType}</span>
											)}
										</span>
										<FiExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-300 transition group-hover:text-sky-600" />
									</Link>
								);
							})}
						</div>
					) : (
						<p className="text-sm text-slate-400">Aucun contenu lié.</p>
					)}
				</div>

				{/* Assignés : chips + édition en place (PUT assignees), gardée par feature:tickets:write. */}
				<AssigneeEditor ticket={ticket} onSaved={onAssigneesSaved} />

				{/* Discussion : fil groupé par auteur, séparateurs de jour, mes messages à droite. */}
				<div>
					<SectionLabel icon={<FiMessageSquare className="h-3 w-3" />}>
						Commentaires{comments.length > 0 ? ` (${comments.length})` : ""}
					</SectionLabel>
					<TicketThread comments={comments} currentUserEmail={currentUserEmail} />
				</div>
			</div>

			{/* Composeur : épinglé au pied du dossier ; désactivé (avec explication) sur un ticket fermé. */}
			{canWrite && (
				<TicketComposer
					value={comment}
					onChange={onCommentChange}
					onSend={onSendComment}
					sending={sendingComment}
					closed={ticket.status === "closed"}
				/>
			)}
		</section>
	);
};

export default TicketDetail;
